/**
 * Orden de profundidad (z) y alineación de elementos del lienzo.
 */

export function widgetZ(widget) {
    const n = Number(widget?.z);
    return Number.isFinite(n) ? n : 0;
}

/** Firma estable para no reaplicar layouts idénticos (evita bucles de RGL). */
export function layoutSignature(items = []) {
    return (items || [])
        .map((item) => `${item.i}:${Math.round(item.x || 0)}:${Math.round(item.y || 0)}:${Math.round(item.w || 0)}:${Math.round(item.h || 0)}`)
        .sort()
        .join('|');
}

export function withZOrder(widgets = []) {
    const ranked = widgets
        .map((widget, index) => ({ widget, index, z: widgetZ(widget) }))
        .sort((a, b) => a.z - b.z || a.index - b.index);
    const zById = {};
    ranked.forEach((item, order) => {
        zById[item.widget.id] = order;
    });
    return widgets.map(widget => ({ ...widget, z: zById[widget.id] ?? 0 }));
}

export function layerWidgets(widgets = [], selectedIds = [], op = 'front') {
    const ids = new Set(selectedIds);
    if (!ids.size) return widgets;
    const ranked = [...withZOrder(widgets)].sort((a, b) => widgetZ(a) - widgetZ(b));
    const selected = ranked.filter(widget => ids.has(widget.id));
    if (!selected.length) return widgets;
    const others = ranked.filter(widget => !ids.has(widget.id));
    let next = ranked;
    if (op === 'front') {
        next = [...others, ...selected];
    } else if (op === 'back') {
        next = [...selected, ...others];
    } else if (op === 'forward') {
        next = ranked.slice();
        for (let i = next.length - 2; i >= 0; i -= 1) {
            if (ids.has(next[i].id) && !ids.has(next[i + 1].id)) {
                const tmp = next[i];
                next[i] = next[i + 1];
                next[i + 1] = tmp;
            }
        }
    } else if (op === 'backward') {
        next = ranked.slice();
        for (let i = 1; i < next.length; i += 1) {
            if (ids.has(next[i].id) && !ids.has(next[i - 1].id)) {
                const tmp = next[i];
                next[i] = next[i - 1];
                next[i - 1] = tmp;
            }
        }
    }
    const ordered = withZOrder(next);
    const zById = Object.fromEntries(ordered.map(widget => [widget.id, widget.z]));
    return widgets.map(widget => (
        zById[widget.id] == null ? widget : { ...widget, z: zById[widget.id] }
    ));
}

export function alignLayoutItems(layout = [], selectedIds = [], mode, bounds = {}) {
    const ids = new Set(selectedIds);
    const items = layout.filter(item => ids.has(item.i));
    if (!items.length) return layout;
    const cols = Math.max(1, Number(bounds.cols) || 12);
    const rows = Math.max(1, Number(bounds.rows) || 20);
    const minX = Math.min(...items.map(item => item.x || 0));
    const minY = Math.min(...items.map(item => item.y || 0));
    const maxX = Math.max(...items.map(item => (item.x || 0) + (item.w || 1)));
    const maxY = Math.max(...items.map(item => (item.y || 0) + (item.h || 1)));
    const box = items.length === 1
        ? { minX: 0, minY: 0, maxX: cols, maxY: rows }
        : { minX, minY, maxX, maxY };

    return layout.map(item => {
        if (!ids.has(item.i)) return item;
        const w = Math.max(1, item.w || 1);
        const h = Math.max(1, item.h || 1);
        let x = item.x || 0;
        let y = item.y || 0;
        if (mode === 'left') x = box.minX;
        if (mode === 'center') x = box.minX + Math.round((box.maxX - box.minX - w) / 2);
        if (mode === 'right') x = box.maxX - w;
        if (mode === 'top') y = box.minY;
        if (mode === 'middle') y = box.minY + Math.round((box.maxY - box.minY - h) / 2);
        if (mode === 'bottom') y = box.maxY - h;
        x = Math.max(0, Math.min(Math.max(0, cols - w), x));
        y = Math.max(0, y);
        return { ...item, x, y };
    });
}

export function canvasGridRows(canvas = {}) {
    const row = Math.max(8, (Number(canvas.rowHeight) || 20) + 4);
    const height = Math.max(row, Number(canvas.height) || 880);
    return Math.max(1, Math.round((height - 24) / row));
}
